import { useEffect, useSyncExternalStore } from "react";

import { API_BASE } from "@/lib/api";
import {
  getServiceHealthSnapshot,
  reportServiceHealthFailure,
  reportServiceHealthSuccess,
  subscribeToServiceHealth,
  type ServiceHealthFailureReason,
  type ServiceHealthState,
} from "@/lib/serviceHealth";

const HEALTH_CHECK_TIMEOUT_MS = 3000;
const HEALTHY_CHECK_INTERVAL_MS = 30_000;
const DEGRADED_CHECK_INTERVAL_MS = 10_000;
const FIRST_FAILURE_RETRY_MS = 1500;
const HEALTH_ENDPOINT = `${API_BASE}/api/health`;

const getServerSnapshot = (): ServiceHealthState => getServiceHealthSnapshot();

const failureReasonForError = (error: unknown): ServiceHealthFailureReason =>
  error instanceof DOMException && error.name === "AbortError"
    ? "timeout"
    : "network";

const nextDelayFor = (snapshot: ServiceHealthState): number => {
  if (snapshot.status === "degraded") return DEGRADED_CHECK_INTERVAL_MS;
  if (snapshot.consecutiveFailures > 0) return FIRST_FAILURE_RETRY_MS;
  return HEALTHY_CHECK_INTERVAL_MS;
};

export const useServiceHealth = (): ServiceHealthState => {
  const snapshot = useSyncExternalStore(
    subscribeToServiceHealth,
    getServiceHealthSnapshot,
    getServerSnapshot
  );

  useEffect(() => {
    if (typeof window === "undefined") return;

    let cancelled = false;
    let timerId: number | undefined;
    let inFlight = false;

    const schedule = (delayMs: number) => {
      if (cancelled) return;
      if (timerId !== undefined) window.clearTimeout(timerId);
      timerId = window.setTimeout(() => {
        void check();
      }, delayMs);
    };

    const check = async () => {
      if (cancelled || inFlight) return;

      if (typeof navigator !== "undefined" && navigator.onLine === false) {
        schedule(nextDelayFor(reportServiceHealthFailure("offline")));
        return;
      }

      inFlight = true;
      const controller = new AbortController();
      const timeoutId = window.setTimeout(
        () => controller.abort(),
        HEALTH_CHECK_TIMEOUT_MS
      );

      try {
        const response = await fetch(HEALTH_ENDPOINT, {
          cache: "no-store",
          headers: { Accept: "application/json" },
          signal: controller.signal,
        });

        const nextState = response.ok
          ? reportServiceHealthSuccess()
          : reportServiceHealthFailure("server");
        schedule(nextDelayFor(nextState));
      } catch (error) {
        schedule(nextDelayFor(reportServiceHealthFailure(failureReasonForError(error))));
      } finally {
        window.clearTimeout(timeoutId);
        inFlight = false;
      }
    };

    const checkSoon = () => {
      schedule(0);
    };

    const checkWhenVisible = () => {
      if (document.visibilityState === "visible") checkSoon();
    };

    window.addEventListener("online", checkSoon);
    window.addEventListener("focus", checkSoon);
    document.addEventListener("visibilitychange", checkWhenVisible);

    void check();

    return () => {
      cancelled = true;
      if (timerId !== undefined) window.clearTimeout(timerId);
      window.removeEventListener("online", checkSoon);
      window.removeEventListener("focus", checkSoon);
      document.removeEventListener("visibilitychange", checkWhenVisible);
    };
  }, []);

  return snapshot;
};
